import * as auditLogHelper from 'helpers/audit-log-helper';
import * as logger from 'logger';
import * as loginApi from 'api/login-api';
import * as connectionStatusService from 'services/connection-status-service';
import { getI18n } from 'services/i18n-service';
import * as tenantService from 'services/tenant-service';
import * as sessionTimeoutService from 'services/session-timeout-service';
import * as tokenService from 'services/token-service';
import * as userService from 'services/user-service';
import { AuditLogType } from 'types/audit-log-type';
import {
  BeginLoginAction,
  LOGIN_ACTION,
  LOGIN_ERROR_ACTION,
  LoginAction,
  LoginErrorAction,
  LOGOUT_ACTION,
  REFRESH_TOKEN_ACTION,
  SwitchToBarcodeLoginAction,
  UserLoggedInAction,
  SetUserPermissionsAction
} from './actions';
import { SetUserProfileAction } from 'pages/user-profile/actions';
import { Dispatch, Middleware, MiddlewareAPI } from 'redux';
import { GetTenantAction } from 'pages/settings/actions';
import { LOGIN_CARD_SCAN_ACTION, ScanAction } from 'components/scanner/actions';
import { LoginResponse } from 'types/api/login-response';
import { LoginType } from 'types/login-type';
import { translations } from 'types/translations';
import { User } from 'types/user';
import { FillTermsAction } from 'pages/terms-of-use/actions';
import { LatestTermsOfUse } from 'types/latest-terms-of-use';
import { usernameSelector } from 'pages/user-profile/selectors';
import { ListNotificationsAction } from 'components/notification/actions';
import { connectionSelector } from 'components/connection-status/selectors';

const LoginMiddleware: Middleware = store => next => async action => {

  try {
    switch (action.type) {
      case LOGIN_ACTION:
        await login(store, action);
        break;

      case REFRESH_TOKEN_ACTION:
        await refreshToken(store);
        break;

      case LOGOUT_ACTION:
        await logout(store);
        break;

      case LOGIN_ERROR_ACTION:
        await sessionTimeoutService.removeSession();
        break;

      case LOGIN_CARD_SCAN_ACTION:
        processScan(store, next, action);
        break;

      default:
        break;
    }
    next(JSON.parse(JSON.stringify(action)));
  }
  catch (e) {
    const error = e as Error;
    logger.error(error, 'LoginMiddleware');
  }
};

async function login(store: MiddlewareAPI, action: LoginAction): Promise<void> {
  const i18n = getI18n();
  const isOnline = await connectionStatusService.getStatus();
  const issueBackgroundToken = await shouldIssueBackgroundToken();

  try {
    store.dispatch(new BeginLoginAction());
    if (!isOnline) {
      const user = await userService.getUser(action.usernameOrBarcode);
      if (!user || !user.token) {
        store.dispatch(new LoginErrorAction(i18n.t(translations.LoginErrorConnectivity)));
        return;
      }
      if (action.loginType === LoginType.UsernamePassword && user.password === action.passwordOrPin) {
        await handleOfflineLoginSuccess(store, user);
      } else if (action.loginType === LoginType.BarcodePin && user.pin === action.passwordOrPin) {
        await handleOfflineLoginSuccess(store, user);
      } else {
        store.dispatch(new LoginErrorAction(i18n.t(translations.InvalidUsernamePassword)));
      }
    } else {
      const tenantId = await tenantService.getTenantId();
      const response = action.loginType === LoginType.UsernamePassword
        ? await loginApi.login(tenantId, action.usernameOrBarcode, action.passwordOrPin, '', '', issueBackgroundToken)
        : await loginApi.login(tenantId, '', '', action.usernameOrBarcode, action.passwordOrPin, issueBackgroundToken);

      if (response.errorMessage) {
        store.dispatch(new LoginErrorAction(i18n.t(translations.Unauthorized) + ' ' + response.errorMessage));
      } else {
        await handleLoginSuccess(store, action, response);
      }
    }
  } catch (e) {
    logger.error(e, 'LoginMiddleware.login');
    store.dispatch(new LoginErrorAction(e.message));
  }
}

async function logout(store: MiddlewareAPI) {
  const state = store.getState();
  auditLogHelper.writeAuditLog(store,
    AuditLogType.User, 'UserLoggedOut', '', `${usernameSelector(state)} logged out.`, connectionSelector(state));
  await sessionTimeoutService.removeSession();
}

async function refreshToken(store: MiddlewareAPI): Promise<void> {
  const isOnline = await connectionStatusService.getStatus();

  try {
    if (!await sessionTimeoutService.isSessionTokenValid(isOnline)) {
      store.dispatch(new LoginErrorAction(''));
      return;
    }

    const jwt = await sessionTimeoutService.refreshSessionToken(isOnline,
      (optelTermsOfUse: LatestTermsOfUse, nmvsTermsOfUse: LatestTermsOfUse) => {
        // Called only when we are online
        store.dispatch(new FillTermsAction(optelTermsOfUse, nmvsTermsOfUse));
      }
    );
    const username = jwt.sub;

    if (!isOnline) {
      fillOfflineAcceptedTerms(store);
    }

    store.dispatch(new GetTenantAction());

    if (isOnline) {
      const tenantId = await tenantService.getTenantId();
      store.dispatch(new ListNotificationsAction(tenantId, username));
    }

    const user = await userService.getUser(username);
    store.dispatch(new SetUserProfileAction(user));
    store.dispatch(new SetUserPermissionsAction(jwt.perm));
  } catch (e) {
    const error = e as Error;
    logger.error(error, 'LoginMiddleware.refreshToken');
    store.dispatch(new LoginErrorAction(error.message));
  }
}

async function shouldIssueBackgroundToken() {
  const existingToken = await tokenService.readToken(tokenService.JWT_BACKGROUND_TOKEN);
  const issueBackgroundToken = existingToken ? false : true;
  return issueBackgroundToken;
}

async function handleLoginSuccess(store: MiddlewareAPI, action: LoginAction, response: LoginResponse) {
  const i18n = getI18n();
  await sessionTimeoutService.renewSession(response.userToken, 
    () => store.dispatch(new LoginErrorAction(i18n.t(translations.SessionTimeout))));

  if (response.backgroundToken) {
    await tokenService.storeToken(tokenService.JWT_BACKGROUND_TOKEN, response.backgroundToken);
  }

  response.user.token = response.userToken;
  response.user.termsAccepted = termsAreNullOrAccepted(response.optelTermsOfUse);

  // response will not return password or pin
  if (action.loginType === LoginType.UsernamePassword) {
    response.user.password = action.passwordOrPin;
  } else {
    response.user.pin = action.passwordOrPin;
  }

  await userService.saveUser(response.user);

  const userPermissions = await tokenService.getUserPermissions();
  store.dispatch(new UserLoggedInAction(userPermissions));
  store.dispatch(new SetUserProfileAction(response.user));
  store.dispatch(new FillTermsAction(response.optelTermsOfUse, response.nmvsTermsOfUse));
  store.dispatch(new GetTenantAction());
  store.dispatch(new ListNotificationsAction(response.tenant.id, response.user.username));

  auditLogHelper.writeAuditLog(store,
    AuditLogType.User, 'UserLoggedIn', '', `${response.user.username} logged in.`, true);
}

async function handleOfflineLoginSuccess(store: MiddlewareAPI, user: User) {
  const i18n = getI18n();
  if (!user.termsAccepted) {
    store.dispatch(new LoginErrorAction(i18n.t(translations.MustBeOnlineToAcceptTermsToLogin)));
    return;
  }
  sessionTimeoutService.setOfflineSessionTimeout();
  await sessionTimeoutService.refreshLastAction();

  if (user.token) {
    await tokenService.storeToken(tokenService.JWT_SESSION_TOKEN, user.token);
  }

  fillOfflineAcceptedTerms(store);
  const userPermissions = await tokenService.getUserPermissions();
  store.dispatch(new UserLoggedInAction(userPermissions));
  store.dispatch(new SetUserProfileAction(user));
  store.dispatch(new GetTenantAction());

  auditLogHelper.writeAuditLog(store,
    AuditLogType.User, 'UserLoggedIn', '', `${user.username} logged in.`, false);
}

function fillOfflineAcceptedTerms(store: MiddlewareAPI) {
  const offlineFillerAcceptedTerms: LatestTermsOfUse = {
    id: 'offline-terms-filler',
    content: '',
    isAccepted: true
  };

  store.dispatch(new FillTermsAction(offlineFillerAcceptedTerms));
}

function processScan(store: MiddlewareAPI, next: Dispatch, action: ScanAction): void {
  if (action.epc) {
    store.dispatch(new SwitchToBarcodeLoginAction(action.epc));
  }
}

function termsAreNullOrAccepted(termsOfUse: LatestTermsOfUse) {
  return termsOfUse === null || termsOfUse === undefined || termsOfUse.isAccepted === true;
}

export default LoginMiddleware;



// WEBPACK FOOTER //
// ./src/pages/login/middleware.ts